import EmptyState from '@/components/linear/empty-state';
import IssueRow from '@/components/linear/issue-row';
import PageToolbar from '@/components/linear/page-toolbar';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import Pagination from '@/components/pagination';
import { myIssuesPath, openIssuePath } from '@/lib/linear-paths';
import { my_issues_path } from '@/rails/routes';
import type { Issue, IssueStatus } from '@/types/issue';
import type { PaginationData } from '@/types/pagination';
import { router, usePage } from '@inertiajs/react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type MineTab = 'assigned' | 'created';

interface Props {
  issues?: Issue[];
  assignedCount?: number;
  createdCount?: number;
  pagination?: PaginationData;
  tab?: MineTab;
}

const COMPLETED_STATUSES = new Set<IssueStatus>(['done', 'canceled', 'duplicate']);

// List rows have no SLA, blocker, or triage fields, so those Focus sections never appear.
const FOCUS_SECTIONS = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'cycle', label: 'Cycle work' },
  { key: 'active', label: 'Other active' },
  { key: 'backlog', label: 'Backlog' },
  { key: 'completed', label: 'Completed' },
] as const;

type FocusKey = (typeof FOCUS_SECTIONS)[number]['key'];

interface FocusSection {
  key: FocusKey;
  label: string;
  issues: Issue[];
}

function focusKey(issue: Issue): FocusKey {
  if (COMPLETED_STATUSES.has(issue.status)) return 'completed';
  if (issue.priority === 1) return 'urgent';
  if (issue.cycle) return 'cycle';
  if (issue.status === 'backlog') return 'backlog';
  return 'active';
}

function startedRank(issue: Issue): number {
  if (issue.status === 'in_progress' || issue.status === 'in_review') return 0;
  return 1;
}

function priorityRank(issue: Issue): number {
  if (issue.priority === 0) return 99;
  return issue.priority;
}

function compareWithinFocus(left: Issue, right: Issue): number {
  const startedDelta = startedRank(left) - startedRank(right);
  if (startedDelta !== 0) return startedDelta;
  const priorityDelta = priorityRank(left) - priorityRank(right);
  if (priorityDelta !== 0) return priorityDelta;
  return left.id.localeCompare(right.id);
}

function focusGroups(issues: Issue[]): FocusSection[] {
  const buckets = new Map<FocusKey, Issue[]>();
  issues.forEach((issue) => {
    const key = focusKey(issue);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(issue);
    } else {
      buckets.set(key, [issue]);
    }
  });

  return FOCUS_SECTIONS.flatMap((section) => {
    const grouped = buckets.get(section.key);
    if (!grouped || grouped.length === 0) return [];
    return [
      { key: section.key, label: section.label, issues: [...grouped].sort(compareWithinFocus) },
    ];
  });
}

function byCreatedAt(issues: Issue[]): Issue[] {
  return [...issues].sort((left, right) => {
    const byDate = right.createdAt.localeCompare(left.createdAt);
    if (byDate !== 0) return byDate;
    return right.id.localeCompare(left.id);
  });
}

export default function Index({
  issues = [],
  assignedCount,
  createdCount,
  pagination,
  tab: tabProp,
}: Props) {
  const url = usePage().url;
  const params = new URLSearchParams(url.split('?')[1] ?? '');
  const tab: MineTab =
    tabProp === 'created' || params.get('tab') === 'created' ? 'created' : 'assigned';
  const assignedTabCount =
    tab === 'assigned' && pagination ? pagination.total : (assignedCount ?? 0);
  const createdTabCount = tab === 'created' && pagination ? pagination.total : (createdCount ?? 0);
  const { selectedIndex, registerItems, setSelectedIndex, items, setCreateIssueOpen } =
    useWorkspaceChrome();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => (tab === 'assigned' ? focusGroups(issues) : []), [issues, tab]);
  const listed = useMemo(
    () => (tab === 'created' ? byCreatedAt(issues) : groups.flatMap((group) => group.issues)),
    [groups, issues, tab]
  );

  useEffect(() => {
    registerItems(
      listed.map((issue) => ({
        id: issue.id,
        identifier: issue.identifier,
        issue,
      }))
    );

    return () => registerItems([]);
  }, [listed, registerItems]);

  const renderIssue = (issue: Issue) => {
    const itemIndex = items.findIndex((item) => item.id === issue.id);
    const selected = itemIndex === selectedIndex;

    return (
      <IssueRow
        key={issue.id}
        issue={issue}
        selected={selected}
        onSelect={(next) => {
          if (itemIndex >= 0) setSelectedIndex(itemIndex);
          router.visit(openIssuePath(next));
        }}
      />
    );
  };

  return (
    <div className="ln-page">
      <PageToolbar
        title="My issues"
        tabs={
          <div className="ln-tabs">
            <button
              type="button"
              className="ln-tab"
              data-active={tab === 'assigned' ? 'true' : 'false'}
              onClick={() => {
                router.get(myIssuesPath(), {}, { preserveState: true, preserveScroll: true });
              }}
            >
              Assigned <span className="ln-tab-count">{assignedTabCount}</span>
            </button>
            <button
              type="button"
              className="ln-tab"
              data-active={tab === 'created' ? 'true' : 'false'}
              onClick={() => {
                router.get(
                  myIssuesPath(),
                  { tab: 'created' },
                  { preserveState: true, preserveScroll: true }
                );
              }}
            >
              Created <span className="ln-tab-count">{createdTabCount}</span>
            </button>
          </div>
        }
      />
      <div className="ln-page-scroll">
        {listed.length === 0 ? (
          <EmptyState description="No issues." />
        ) : tab === 'created' ? (
          listed.map((issue) => renderIssue(issue))
        ) : (
          groups.map((group) => {
            const isCollapsed = collapsed[group.key] === true;

            return (
              <section key={group.key}>
                <div className="ln-group-header">
                  <button
                    type="button"
                    className="ln-group-toggle"
                    onClick={() =>
                      setCollapsed((current) => ({
                        ...current,
                        [group.key]: !current[group.key],
                      }))
                    }
                  >
                    {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                    <span>{group.label}</span>
                    <span className="ln-group-count">{group.issues.length}</span>
                  </button>
                  <button
                    type="button"
                    className="ln-group-add"
                    aria-label={`Create ${group.label} issue`}
                    onClick={() => setCreateIssueOpen(true)}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {isCollapsed ? null : group.issues.map((issue) => renderIssue(issue))}
              </section>
            );
          })
        )}
        {pagination ? (
          <Pagination
            pagination={pagination}
            pathBuilder={(pageParams) =>
              my_issues_path(pageParams as { page?: number; tab?: string })
            }
            queryParams={tab === 'created' ? { tab: 'created' } : {}}
            preserveScroll
            preserveState
          />
        ) : null}
      </div>
    </div>
  );
}
